import { LineupPlayer, GameLineups as GameLineupsType } from "@/hooks/useGameLineups";
import { PlayerRatingsMap, getRatingColorClass, getTrendIndicator } from "@/hooks/usePlayerRatings";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import footballGoalIcon from "@/assets/icons/football-goal.png";

// ============= Types =============
export interface PlayerMatchEvents {
  goals: number;
  yellowCards: number;
  redCards: number;
  wasSubstituted: boolean;
  substituteMinute?: number;
}

// ============= Constants for Layout Contract =============
// These are "contract" values, not magic numbers - they define the layout guarantee
const OVERLAY_HEIGHT = {
  mobile: 32,  // px - height of header/footer overlay on mobile
  desktop: 36, // px - height of header/footer overlay on desktop
};

// ============= Team Average Rating Calculation =============

// Calculate average rating of 11 starting players
const calculateTeamAverageRating = (
  players: LineupPlayer[],
  ratingsMap?: PlayerRatingsMap,
  useMatchRating: boolean = false
): number | null => {
  if (!ratingsMap || players.length === 0) return null;
  
  let sum = 0;
  let count = 0;
  
  for (const player of players) {
    if (!player.player_id) continue;
    const data = ratingsMap.get(player.player_id);
    if (!data) continue;
    
    // Prioritize match rating if available, otherwise use historical average
    const rating = useMatchRating && data.currentRating !== null 
      ? data.currentRating 
      : data.avgRating;
    
    if (rating !== null && rating !== undefined) {
      sum += rating;
      count++;
    }
  }
  
  if (count < 5) return null; // Minimum 5 players with rating
  return sum / count;
};

// Team Average Badge component (FlashScore style) - positioned inside overlay
const TeamAverageBadge = ({ 
  rating, 
  isHome 
}: { 
  rating: number | null; 
  isHome: boolean;
}) => {
  if (rating === null) return null;
  
  // Determine color based on rating
  const colorClass = rating >= 7.0 
    ? 'bg-emerald-500/90 text-white' 
    : rating >= 6.0 
      ? 'bg-amber-500/90 text-white' 
      : 'bg-red-500/90 text-white';
  
  return (
    <div className={cn(
      "px-1.5 py-0.5 rounded-md shadow-md",
      "text-[10px] sm:text-[11px] font-bold",
      colorClass,
      // No absolute positioning - it's inline within the overlay bar
    )}>
      <span className="opacity-70">Ø</span> {rating.toFixed(1)}
    </div>
  );
};

interface LineupPitchViewProps {
  lineups: GameLineupsType;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  ratingsMap?: PlayerRatingsMap;
  isLive?: boolean;
  hasFinishedSnapshots?: boolean;
  playerEventsMap?: Map<number, PlayerMatchEvents>;
  bestPlayerId?: number;
}

// ============= Visual Indicator Icons (FlashScore Style) =============

// Split circle: red (out) + green (in) - FlashScore substitution style
// Using fluorescent colors for better contrast against green pitch
const SubstitutionIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 16 16" 
    className={cn("w-3.5 h-3.5 md:w-4 md:h-4", className)}
  >
    {/* Subtle outline */}
    <circle cx="8" cy="8" r="7" fill="none" stroke="#9ca3af" strokeWidth="0.5" />
    {/* Left half: red (player out) - fluorescent */}
    <path d="M8 1 A7 7 0 0 0 8 15" fill="none" stroke="#ff4040" strokeWidth="3" />
    {/* Right half: green (player in) - fluorescent for contrast with pitch */}
    <path d="M8 1 A7 7 0 0 1 8 15" fill="none" stroke="#00e676" strokeWidth="3" />
  </svg>
);

// Football goal icon - PNG with transparent background
const FootballIcon = ({ className }: { className?: string }) => (
  <img 
    src={footballGoalIcon} 
    alt="Goal" 
    className={cn("w-4 h-4 md:w-5 md:h-5", className)}
  />
);

// Golden star icon - separator between average and game rating
const GameRatingStar = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 12 12" 
    className={cn("w-3 h-3 md:w-3.5 md:h-3.5", className)}
  >
    <path 
      d="M6 1 L7.2 4.2 L10.5 4.5 L8 6.8 L8.8 10 L6 8.2 L3.2 10 L4 6.8 L1.5 4.5 L4.8 4.2 Z" 
      fill="#FFD700" 
      stroke="#DAA520" 
      strokeWidth="0.3"
    />
  </svg>
);

// Average rating icon - small bar chart
const AvgIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-2 h-2 md:w-2.5 md:h-2.5", className)}>
    <rect x="1" y="6" width="2" height="5" fill="currentColor" opacity="0.6"/>
    <rect x="5" y="3" width="2" height="8" fill="currentColor" opacity="0.8"/>
    <rect x="9" y="5" width="2" height="6" fill="currentColor" opacity="0.7"/>
  </svg>
);

// Trend arrow UP - performance above expected (green) - THICK SVG
const TrendUpArrow = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-3 h-3 md:w-3.5 md:h-3.5", className)}>
    <path 
      d="M6 2 L6 10 M6 2 L3 5 M6 2 L9 5" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Trend arrow DOWN - performance below expected (red) - THICK SVG
const TrendDownArrow = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-3 h-3 md:w-3.5 md:h-3.5", className)}>
    <path 
      d="M6 10 L6 2 M6 10 L3 7 M6 10 L9 7" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Trend arrow NEUTRAL - expected performance (yellow) - THICK SVG
const TrendNeutralArrow = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 12 12" className={cn("w-3 h-3 md:w-3.5 md:h-3.5", className)}>
    <path 
      d="M2 6 L10 6 M10 6 L7 3 M10 6 L7 9" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Goal icon with counter - positioned closer to player photo
const GoalIcon = ({ count, className }: { count: number; className?: string }) => (
  <div className={cn(
    "absolute -top-1 -right-1.5 md:-right-2 flex items-center justify-center",
    className
  )}>
    <FootballIcon />
    {count > 1 && (
      <span className="absolute -top-1 -right-1 text-[7px] md:text-[8px] 
                       bg-primary text-white rounded-full w-3 h-3 
                       flex items-center justify-center font-bold">
        {count}
      </span>
    )}
  </div>
);

// Yellow card - CSS rectangle - positioned on left side to avoid substitution overlap
const YellowCardIcon = ({ className }: { className?: string }) => (
  <div className={cn(
    "absolute top-1/2 -translate-y-1/2 -left-2.5 w-1.5 h-2 md:w-2 md:h-2.5",
    "bg-yellow-400 rounded-[1px] shadow-sm border border-yellow-500",
    className
  )} />
);

// Red card - CSS rectangle - positioned on left side to avoid substitution overlap
const RedCardIcon = ({ className }: { className?: string }) => (
  <div className={cn(
    "absolute top-1/2 -translate-y-1/2 -left-2.5 w-1.5 h-2 md:w-2 md:h-2.5",
    "bg-red-500 rounded-[1px] shadow-sm border border-red-600",
    className
  )} />
);

// ============= Helper Functions =============

// Abbreviate name: "Cristiano Ronaldo" → "C. Ronaldo"
const abbreviateName = (fullName: string): string => {
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return fullName;
  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
};

// Parse grid string "row:col" into numbers
const parseGrid = (grid: string | null | undefined): { row: number; col: number } => {
  if (!grid) return { row: 1, col: 1 };
  const [row, col] = grid.split(':').map(Number);
  return { row: row || 1, col: col || 1 };
};

// Group players by their grid row
const groupPlayersByRow = (players: LineupPlayer[]): Map<number, LineupPlayer[]> => {
  const grouped = new Map<number, LineupPlayer[]>();
  
  players.forEach(player => {
    if (!player.grid) return; // Skip players without grid data
    const { row } = parseGrid(player.grid);
    if (!grouped.has(row)) grouped.set(row, []);
    grouped.get(row)!.push(player);
  });
  
  // Sort players within each row by column
  grouped.forEach((rowPlayers) => {
    rowPlayers.sort((a, b) => {
      const colA = parseGrid(a.grid).col;
      const colB = parseGrid(b.grid).col;
      return colA - colB;
    });
  });
  
  return grouped;
};

// Count unique rows in a formation for dynamic aspect ratio calculation
const countFormationRows = (players: LineupPlayer[]): number => {
  const rows = new Set<number>();
  players.forEach(player => {
    const { row } = parseGrid(player.grid);
    rows.add(row);
  });
  return rows.size;
};

// ============= PitchPlayer Component =============
const PitchPlayer = ({ 
  player, 
  isHome,
  size = 'normal',
  isCompact = false,
  ratingsMap,
  isLive = false,
  hasFinishedSnapshots = false,
  playerEventsMap,
  bestPlayerId
}: { 
  player: LineupPlayer; 
  isHome: boolean;
  size?: 'small' | 'normal';
  isCompact?: boolean; // New: compact mode for 5+ row formations
  ratingsMap?: PlayerRatingsMap;
  isLive?: boolean;
  hasFinishedSnapshots?: boolean;
  playerEventsMap?: Map<number, PlayerMatchEvents>;
  bestPlayerId?: number;
}) => {
  const { t } = useTranslation('analysis');
  const photoUrl = player.photo || 
    (player.player_id ? `https://media.api-sports.io/football/players/${player.player_id}.png` : null);

  // Get rating data for this player
  const ratingData = player.player_id ? ratingsMap?.get(player.player_id) : undefined;
  const hasLiveRating = ratingData?.currentRating !== null && ratingData?.currentRating !== undefined;
  const hasHistorical = ratingData?.avgRating !== null && ratingData?.avgRating !== undefined;
  const showLiveRating = (isLive || hasFinishedSnapshots) && hasLiveRating;
  const displayRating = showLiveRating 
    ? ratingData?.currentRating 
    : ratingData?.avgRating;
  const trend = showLiveRating ? getTrendIndicator(ratingData?.trend || null) : null;
  
  // Get events for this player
  const playerEvents = player.player_id 
    ? playerEventsMap?.get(player.player_id) 
    : undefined;
  
  // Check if this is the best player (Man of the Match)
  const isBestPlayer = player.player_id === bestPlayerId && (isLive || hasFinishedSnapshots);
  
  // Size classes: use small in compact mode OR when row has many players
  const effectiveSize = isCompact ? 'small' : size;
  const sizeClasses = effectiveSize === 'small' 
    ? 'w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10' 
    : 'w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11';
  
  const numberSize = effectiveSize === 'small' 
    ? 'w-3 h-3 text-[8px] sm:w-3.5 sm:h-3.5 sm:text-[9px]' 
    : 'w-3.5 h-3.5 text-[9px] sm:w-4 sm:h-4 sm:text-[10px]';

  // Container height: compact mode uses smaller min-height to fit 5 rows
  const containerClasses = isCompact
    ? "min-h-[46px] sm:min-h-[56px] md:min-h-[68px]"
    : "min-h-[52px] sm:min-h-[60px] md:min-h-[72px]";

  // Gap between elements: tighter in compact mode
  const gapClass = isCompact ? "gap-0" : "gap-0.5";

  const playerContent = (
    <div className={cn("flex flex-col items-center justify-center", containerClasses, gapClass)}>
      <div className="relative">
        {photoUrl ? (
          <img 
            src={photoUrl}
            alt={player.name}
            className={cn(
              sizeClasses,
              "rounded-full border-2 object-cover bg-muted",
              isHome ? "border-blue-400" : "border-red-400"
            )}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        {/* Fallback with number */}
        <div 
          className={cn(
            sizeClasses,
            "rounded-full border-2 items-center justify-center font-bold text-white text-xs",
            isHome ? "bg-blue-600 border-blue-400" : "bg-red-600 border-red-400",
            photoUrl ? "hidden" : "flex"
          )}
        >
          {player.number}
        </div>
        
        {/* Event indicators */}
        {/* Substitution icon (top-left corner, offset to avoid face overlap) */}
        {playerEvents?.wasSubstituted && (
          <div className="absolute -top-1 -left-3 md:-left-3.5">
            <SubstitutionIcon />
          </div>
        )}
        
        {/* Goal icon (top-right corner) */}
        {playerEvents && playerEvents.goals > 0 && (
          <GoalIcon count={playerEvents.goals} />
        )}
        
        {/* Card icon (left side) - red takes priority over yellow */}
        {playerEvents?.redCards && playerEvents.redCards > 0 ? (
          <RedCardIcon />
        ) : playerEvents?.yellowCards && playerEvents.yellowCards > 0 ? (
          <YellowCardIcon />
        ) : null}
        
        {/* Number badge */}
        {photoUrl && (
          <span className={cn(
            "absolute -bottom-0.5 -right-0.5 rounded-full flex items-center justify-center font-bold text-white",
            numberSize,
            isHome ? "bg-blue-600" : "bg-red-600"
          )}>
            {player.number}
          </span>
        )}
      </div>
      
      {/* Name and optional rating badge */}
      <div className="flex flex-col items-center">
        <span className={cn(
          "font-medium text-center max-w-12 sm:max-w-14 md:max-w-24 truncate px-0.5 sm:px-1 py-0.5 rounded",
          "text-white shadow-sm",
          isHome ? "bg-blue-900/90" : "bg-red-900/90",
          // Smaller text in compact mode
          isCompact ? "text-[8px] sm:text-[9px] md:text-[10px]" : "text-[9px] sm:text-[10px] md:text-[11px]"
        )}>
          {abbreviateName(player.name)}
        </span>
        
        {/* Rating badge - Shows BOTH average and match rating */}
        {(hasHistorical || showLiveRating) && (
          <span className={cn(
            "font-bold px-1 sm:px-1.5 rounded mt-0.5 inline-flex items-center gap-0.5 sm:gap-1",
            isBestPlayer 
              // Man of the Match: golden, EXTRA BOLD, larger star - compact on mobile
              ? cn(
                  "font-extrabold bg-amber-400 text-amber-900 shadow-xl ring-2 ring-amber-500/70",
                  isCompact ? "text-[10px] sm:text-[12px] md:text-[14px] px-1.5 sm:px-2 py-0.5" : "text-[12px] sm:text-[14px] md:text-[16px] px-2 sm:px-3 py-0.5 sm:py-1"
                )
              // Normal: standard size
              : cn(
                  "bg-black/70 backdrop-blur-sm",
                  isCompact ? "text-[8px] md:text-[9px]" : "text-[9px] md:text-[10px]"
                )
          )}>
            {/* Historical average first (smaller, WHITE for contrast) - only for non-best players during live/finished */}
            {/* Hidden on mobile to keep pitch view clean */}
            {hasHistorical && showLiveRating && !isBestPlayer && (
              <span className="hidden sm:inline-flex items-center gap-0.5 text-[8px] md:text-[9px] text-white/90">
                <AvgIcon className="text-white/80" />
                {ratingData!.avgRating!.toFixed(1)}
              </span>
            )}
            
            {/* Golden star separator between average and game rating */}
            {hasHistorical && showLiveRating && !isBestPlayer && (
              <GameRatingStar />
            )}
            
            {/* Match rating (larger, colored + trend arrow) */}
            {showLiveRating && ratingData?.currentRating && (
              <span className={cn(
                "inline-flex items-center font-semibold",
                isBestPlayer ? "" : getRatingColorClass(ratingData.currentRating),
                isCompact ? "text-[10px] md:text-[11px]" : "text-[11px] md:text-[12px]"
              )}>
                {ratingData.currentRating.toFixed(1)}
                {/* Trend arrow - THICK SVG */}
                {!isBestPlayer && trend?.icon && (
                  <span className={cn("ml-0.5", trend.colorClass)}>
                    {trend.icon === '↑' && <TrendUpArrow />}
                    {trend.icon === '↓' && <TrendDownArrow />}
                    {trend.icon === '→' && <TrendNeutralArrow />}
                  </span>
                )}
              </span>
            )}
            
            {/* Only show average if no match rating available (pre-game) - WHITE for contrast */}
            {!showLiveRating && hasHistorical && (
              <span className="inline-flex items-center gap-0.5 text-white">
                <AvgIcon className="text-white/80" />
                {ratingData!.avgRating!.toFixed(1)}
              </span>
            )}
            
            {/* Star icon for Man of the Match - LARGER */}
            {isBestPlayer && (
              <Star className={cn(
                "fill-amber-900",
                isCompact ? "w-3 h-3 md:w-3.5 md:h-3.5" : "w-3.5 h-3.5 md:w-4 md:h-4"
              )} />
            )}
          </span>
        )}
      </div>
    </div>
  );

  // Wrap with tooltip if we have rating data
  if (ratingData) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div className="cursor-help">{playerContent}</div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-52">
            <div className="space-y-1 text-xs">
              <p className="font-bold">{player.name}</p>
              {showLiveRating && ratingData.currentRating !== null && (
                <p className="font-semibold">
                  {t('analysis:playerRating.gameRating', { rating: ratingData.currentRating.toFixed(1) })}
                  {ratingData.snapshotMinute && (
                    <span className="text-muted-foreground ml-1">
                      ({ratingData.snapshotMinute}')
                    </span>
                  )}
                </p>
              )}
              {hasHistorical && (
                <p className="text-muted-foreground">
                  {t('analysis:playerRating.average', { count: ratingData.gamesPlayed, rating: ratingData.avgRating?.toFixed(1) })}
                </p>
              )}
              {trend?.label && (
                <p className={trend.colorClass}>
                  {trend.icon} {trend.label}
                </p>
              )}
              {isBestPlayer && (
                <p className="text-amber-600 font-semibold flex items-center gap-1">
                  <Star className="w-3 h-3 fill-amber-500" /> Man of the Match
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return playerContent;
};

// ============= TeamFormation Component =============
const TeamFormation = ({ 
  players, 
  isHome, 
  formation,
  teamName,
  teamLogo,
  ratingsMap,
  isLive,
  hasFinishedSnapshots,
  playerEventsMap,
  bestPlayerId
}: { 
  players: LineupPlayer[]; 
  isHome: boolean;
  formation: string;
  teamName: string;
  teamLogo?: string;
  ratingsMap?: PlayerRatingsMap;
  isLive?: boolean;
  hasFinishedSnapshots?: boolean;
  playerEventsMap?: Map<number, PlayerMatchEvents>;
  bestPlayerId?: number;
}) => {
  const groupedPlayers = groupPlayersByRow(players);
  const rows = Array.from(groupedPlayers.keys()).sort((a, b) => a - b);
  
  // Both teams use same row order (1→N), direction controlled by flex-col vs flex-col-reverse
  const displayRows = rows;
  
  // Density mode: >= 5 rows uses justify-between + compact players for better space usage
  const isDense = displayRows.length >= 5;

  return (
    <div className="flex flex-col h-full">
      {/* Player rows - justify-evenly for normal formations, justify-between for dense */}
      <div className={cn(
        "flex-1 flex min-h-0",
        isDense ? "justify-between" : "justify-evenly",
        // Padding classes: in dense mode, asymmetric to anchor to edges
        isDense
          ? (isHome 
              ? "pt-1 sm:pt-1.5 md:pt-2 pb-0.5 sm:pb-1 md:pb-1.5" // HOME: anchor to top
              : "pb-1 sm:pb-1.5 md:pb-2 pt-0.5 sm:pt-1 md:pt-1.5"  // AWAY: anchor to bottom
            )
          : "py-1 sm:py-2 md:py-3", // Normal: symmetric
        isHome ? "flex-col" : "flex-col-reverse"
      )}>
        {displayRows.map((rowNum) => {
          const rowPlayers = groupedPlayers.get(rowNum) || [];
          // Use justify-evenly for full width distribution, center only for GK (1 player)
          const justifyClass = rowPlayers.length === 1 ? "justify-center" : "justify-evenly";
          // Extra bottom margin for GK row (1 player) in away team with dense formation
          // This pushes GK upward so rating badge stays within pitch boundaries
          const isGkRow = rowPlayers.length === 1;
          const needsGkLift = !isHome && isDense && isGkRow;
          
          return (
            <div 
              key={rowNum} 
              className={cn(
                "flex items-center px-2 md:px-4 min-h-0",
                justifyClass,
                // Lift GK row in dense away formations
                needsGkLift && "mb-3 sm:mb-4 md:mb-5"
              )}
            >
              {rowPlayers.map((player, idx) => (
                <PitchPlayer 
                  key={player.player_id || idx} 
                  player={player} 
                  isHome={isHome}
                  size={rowPlayers.length > 4 ? 'small' : 'normal'}
                  isCompact={isDense}
                  ratingsMap={ratingsMap}
                  isLive={isLive}
                  hasFinishedSnapshots={hasFinishedSnapshots}
                  playerEventsMap={playerEventsMap}
                  bestPlayerId={bestPlayerId}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============= Overlay Header Bar Component =============
const OverlayHeaderBar = ({
  teamName,
  teamLogo,
  formation,
  avgRating,
  isHome
}: {
  teamName: string;
  teamLogo?: string;
  formation: string;
  avgRating: number | null;
  isHome: boolean;
}) => (
  <div className={cn(
    "absolute left-2 right-2 z-20 pointer-events-none",
    "flex items-center justify-between px-2 sm:px-3",
    "h-[28px] sm:h-[32px]",
    isHome ? "top-2" : "bottom-2"
  )}>
    {/* Team info - LEFT for home, RIGHT for away */}
    <div className={cn(
      "flex items-center gap-1.5 sm:gap-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]",
      !isHome && "order-2"
    )}>
      {teamLogo && (
        <img 
          src={teamLogo} 
          alt={teamName} 
          className="w-4 h-4 sm:w-5 sm:h-5 object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" 
        />
      )}
      <span className="text-[10px] sm:text-xs font-semibold text-white truncate max-w-20 sm:max-w-28">
        {teamName}
      </span>
      <span className="text-[9px] sm:text-[10px] text-white/80 font-mono">
        {formation}
      </span>
    </div>
    
    {/* Average rating badge - opposite side */}
    <div className={cn(!isHome && "order-1")}>
      <TeamAverageBadge rating={avgRating} isHome={isHome} />
    </div>
  </div>
);

// ============= Main Component =============
export function LineupPitchView({
  lineups,
  homeTeamName,
  awayTeamName,
  homeTeamLogo,
  awayTeamLogo,
  ratingsMap,
  isLive = false,
  hasFinishedSnapshots = false,
  playerEventsMap,
  bestPlayerId
}: LineupPitchViewProps) {
  // Determine if we should use match ratings or historical
  const useMatchRating = isLive || hasFinishedSnapshots;
  
  // Calculate team average ratings
  const homeTeamAvgRating = calculateTeamAverageRating(
    lineups.home_startxi, 
    ratingsMap, 
    useMatchRating
  );
  const awayTeamAvgRating = calculateTeamAverageRating(
    lineups.away_startxi, 
    ratingsMap, 
    useMatchRating
  );

  // Calculate total rows to determine pitch height on mobile
  // Complex formations (3-4-2-1, 3-4-1-2) need taller pitch
  const homeRows = countFormationRows(lineups.home_startxi);
  const awayRows = countFormationRows(lineups.away_startxi);
  const totalRows = homeRows + awayRows;

  // Dynamic aspect ratio: more rows = taller pitch on mobile
  // Using inline style to avoid Tailwind purge issues with dynamic classes
  // Values: 0.38 (10+ rows), 0.42 (9 rows), 0.48 (8 or fewer rows)
  const mobileAspectRatio = totalRows >= 10 ? 0.38 : totalRows >= 9 ? 0.42 : 0.48;

  return (
    <div 
      className="relative w-full max-w-2xl mx-auto rounded-xl overflow-hidden shadow-lg sm:aspect-[9/16] md:aspect-[2/3]"
      style={{ aspectRatio: `1/${1/mobileAspectRatio}` }}
    >
      {/* Football pitch background */}
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-600 via-emerald-500 to-emerald-600">
        {/* Pitch markings */}
        <svg 
          viewBox="0 0 100 140" 
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="none"
        >
          {/* Outer border */}
          <rect x="5" y="5" width="90" height="130" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          
          {/* Center line */}
          <line x1="5" y1="70" x2="95" y2="70" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          
          {/* Center circle */}
          <circle cx="50" cy="70" r="12" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          <circle cx="50" cy="70" r="0.8" fill="white" fillOpacity="0.6" />
          
          {/* Top penalty area */}
          <rect x="25" y="5" width="50" height="20" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          <rect x="35" y="5" width="30" height="8" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          <circle cx="50" cy="17" r="0.8" fill="white" fillOpacity="0.6" />
          
          {/* Bottom penalty area */}
          <rect x="25" y="115" width="50" height="20" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          <rect x="35" y="127" width="30" height="8" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          <circle cx="50" cy="123" r="0.8" fill="white" fillOpacity="0.6" />
          
          {/* Corner arcs */}
          <path d="M 5 8 A 3 3 0 0 0 8 5" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          <path d="M 92 5 A 3 3 0 0 0 95 8" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          <path d="M 5 132 A 3 3 0 0 1 8 135" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
          <path d="M 92 135 A 3 3 0 0 1 95 132" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.6" />
        </svg>
        
        {/* Grass pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          {Array.from({ length: 14 }).map((_, i) => (
            <div 
              key={i} 
              className={cn(
                "h-[7.14%]",
                i % 2 === 0 ? "bg-black/10" : "bg-white/5"
              )} 
            />
          ))}
        </div>
      </div>
      
      {/* ABSOLUTE OVERLAYS - Header bars that NEVER get pushed out */}
      <OverlayHeaderBar
        teamName={homeTeamName}
        teamLogo={homeTeamLogo}
        formation={lineups.home_formation}
        avgRating={homeTeamAvgRating}
        isHome={true}
      />
      <OverlayHeaderBar
        teamName={awayTeamName}
        teamLogo={awayTeamLogo}
        formation={lineups.away_formation}
        avgRating={awayTeamAvgRating}
        isHome={false}
      />
      
      {/* Players container - with padding to reserve space for overlays */}
      <div 
        className="relative z-10 h-full flex flex-col"
        style={{
          // Reserve space for overlay headers (top and bottom)
          paddingTop: `${OVERLAY_HEIGHT.mobile + 8}px`,
          paddingBottom: `${OVERLAY_HEIGHT.mobile + 8}px`,
        }}
      >
        {/* HOME team players - USEFUL ZONE with overflow control */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TeamFormation
            players={lineups.home_startxi}
            isHome={true}
            formation={lineups.home_formation}
            teamName={homeTeamName}
            teamLogo={homeTeamLogo}
            ratingsMap={ratingsMap}
            isLive={isLive}
            hasFinishedSnapshots={hasFinishedSnapshots}
            playerEventsMap={playerEventsMap}
            bestPlayerId={bestPlayerId}
          />
        </div>
        
        {/* Divider line at midfield */}
        <div className="h-px bg-white/20 shrink-0" />
        
        {/* AWAY team players - USEFUL ZONE with overflow control */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TeamFormation
            players={lineups.away_startxi}
            isHome={false}
            formation={lineups.away_formation}
            teamName={awayTeamName}
            teamLogo={awayTeamLogo}
            ratingsMap={ratingsMap}
            isLive={isLive}
            hasFinishedSnapshots={hasFinishedSnapshots}
            playerEventsMap={playerEventsMap}
            bestPlayerId={bestPlayerId}
          />
        </div>
      </div>
    </div>
  );
}
